import { GridSortDirection } from '@mui/x-data-grid';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MAX_ENTITIES_NUMBER_PER_REQUEST, NUMBER_OF_RETRIES } from 'common/constants/exportData';
import { getNumberOfRequests, mergeResults } from 'common/utils/exportDataApiHelper';
import { mapUserMembershipDetails } from 'common/utils/userMembershipMapper';

import { APIUserGroupsUserPost, APIUserGroupsUsersDelete, APIUserGroupsUsersGetList, APIUserGroupsUsersGetParams, APIUserGroupsUsersGetSingleResponse } from './userGroupsUsers.api';

const _formatUserGroupsUsersResponse = (rows: APIUserGroupsUsersGetSingleResponse[]) =>
    rows.map((item: APIUserGroupsUsersGetSingleResponse) =>
    {
        return {
            ...item,
            ...mapUserMembershipDetails(item),
        };
    }
    );

export const fetchUserGroupsUsers = createAsyncThunk('user-groups/fetchUserGroupsUsers', async (item: APIUserGroupsUsersGetParams) => {
    const response = await APIUserGroupsUsersGetList(item);
    return {
        data: _formatUserGroupsUsersResponse(response.data),
        skipped: response.skipped,
        taken: response.taken,
        count: response.count,
        requestPayload: {
            userGroupId: item.usergroupId,
            tenantId: item.tenantId
        }
    };
});

export const fetchUserGroupsUsersForExport = createAsyncThunk('user-groups/fetchUserGroupsAllUsers', async (item: {
    tenantId: string,
    usergroupId: string,
    isActive?: boolean,
    skip?: number,
    take?: number,
    search?: string,
    sortKey?: string,
    sortOrder?: GridSortDirection,
    rowsCount?: number,
    signal: AbortSignal,
    status?:string[]
 }) => {
    const numberOfRequests = getNumberOfRequests(item.rowsCount);
    const fetchPromises = Array.from({ length: numberOfRequests }, async (_, i) => APIUserGroupsUsersGetList({
        ...item,
        skip: i * MAX_ENTITIES_NUMBER_PER_REQUEST,
        take: MAX_ENTITIES_NUMBER_PER_REQUEST,
        retries: NUMBER_OF_RETRIES
    }));

    const response = (await Promise.all(fetchPromises))
        .map((response)=>{
            return {
                data: _formatUserGroupsUsersResponse(response.data),
                skipped: response.skipped,
                taken: response.taken
            };
        });

    return mergeResults(response);
});

export const postUserGroupUserMembership = createAsyncThunk('user-groups/postUserGroupUser', async (item: {
    tenantId: string,
    userGroupId: string,
    userId: string,
    paginationInfo: {
        skip: number;
        take: number;
        search: string;
    },
    status: string[]
})=> {
    const response = await APIUserGroupsUserPost(item);
    const gridData = await APIUserGroupsUsersGetList({
        tenantId: item.tenantId,
        usergroupId: item.userGroupId,
        skip: item.paginationInfo.skip,
        take: item.paginationInfo.take,
        search: item.paginationInfo.search,
        status: item.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = _formatUserGroupsUsersResponse(data);
    return {
        item,
        response,
        rows,
        count,
        skipped,
        taken,
    };
});


export const deleteUserGroupUser = createAsyncThunk('user-groups/deleteUserGroupUser', async (item: {
    userGroupId: string,
    tenantId: string,
    userId: string,
    skip?: number,
    take?: number,
    search?: string,
    sortKey?: string,
    sortOrder?: GridSortDirection,
    status?: string[]
})=> {
    const response = await APIUserGroupsUsersDelete({ tenantId: item.tenantId, userId: item.userId, userGroupId: item.userGroupId });
    const gridData = await APIUserGroupsUsersGetList({
        tenantId: item.tenantId,
        usergroupId: item.userGroupId,
        skip: item.skip,
        take: item.take,
        search: item.search,
        sortKey: item.sortKey,
        sortOrder: item.sortOrder,
        status: item.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = _formatUserGroupsUsersResponse(data);

    return {
        item,
        response,
        rows,
        count,
        skipped,
        taken,
    };
});
